import { ref, onMounted, onUnmounted } from 'vue';

export interface DropHandlers {
  onText?: (text: string) => void;
  onUrl?: (url: string) => void;
  onFile?: (file: File) => void;
}

export function useDragDrop(handlers: DropHandlers = {}) {
  const isDragging = ref(false);
  const dragCounter = ref(0);

  function handleDragEnter(e: DragEvent) {
    e.preventDefault();
    dragCounter.value++;
    if (dragCounter.value === 1) {
      isDragging.value = true;
    }
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault();
    dragCounter.value--;
    if (dragCounter.value === 0) {
      isDragging.value = false;
    }
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    // 显示拖拽效果
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDragging.value = false;
    dragCounter.value = 0;

    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) return;

    // 1. 处理文本
    const text = dataTransfer.getData('text/plain');
    if (text) {
      // 检查是否是 URL
      if (isValidUrl(text)) {
        handlers.onUrl?.(text);
      } else {
        handlers.onText?.(text);
      }
      return;
    }

    // 2. 处理文件
    const files = dataTransfer.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        handlers.onFile?.(files[i]);
      }
      return;
    }

    // 3. 处理 HTML（例如从网页拖拽链接）
    const html = dataTransfer.getData('text/html');
    if (html) {
      // 提取 URL
      const urlMatch = html.match(/href=["']([^"']+)["']/);
      if (urlMatch && urlMatch[1]) {
        handlers.onUrl?.(urlMatch[1]);
        return;
      }
    }

    // 4. 处理 URI（例如从其他应用拖拽）
    const uri = dataTransfer.getData('text/uri-list');
    if (uri && isValidUrl(uri)) {
      handlers.onUrl?.(uri);
      return;
    }
  }

  function isValidUrl(str: string): boolean {
    try {
      const url = new URL(str);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  function setupGlobalDragDrop() {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);
  }

  function cleanupGlobalDragDrop() {
    window.removeEventListener('dragenter', handleDragEnter);
    window.removeEventListener('dragleave', handleDragLeave);
    window.removeEventListener('dragover', handleDragOver);
    window.removeEventListener('drop', handleDrop);
  }

  return {
    isDragging,
    setupGlobalDragDrop,
    cleanupGlobalDragDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
